import React, { useEffect, useMemo, useState } from "react";

import { api } from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export default function AddProspectDialog({ open, onOpenChange, householdId, onAdded }) {
  const [name, setName] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (open) {
      setName("");
      setSelectedLeadId(null);
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const q = String(name || "").trim();
    if (!open) return;
    if (q.length < 2) {
      setResults([]);
      return;
    }

    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get("/leads/search", { params: { q, limit: 10 } });
        setResults(res.data?.items || []);
      } finally {
        setSearching(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [name, open]);

  const canSubmit = useMemo(() => String(name || "").trim().length > 0, [name]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Prospect</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-sm text-slate-200/70">Start typing a name to link an existing lead, or submit to create a new lead.</div>

          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSelectedLeadId(null);
            }}
            className="bg-white/5 ring-1 ring-white/10"
            placeholder="Prospect name"
          />

          <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
            <div className="border-b border-white/10 px-4 py-2 text-xs font-semibold text-slate-200/70">Matches</div>
            <div className="max-h-[220px] overflow-auto">
              {searching ? <div className="px-4 py-3 text-sm text-slate-200/70">Searching…</div> : null}
              {!searching && results.length === 0 ? <div className="px-4 py-3 text-sm text-slate-200/70">No matches.</div> : null}
              {results.map((r) => {
                const active = r.id === selectedLeadId;
                return (
                  <button
                    key={r.id}
                    type="button"
                    className={
                      "w-full px-4 py-3 text-left text-sm hover:bg-white/10 " +
                      (active ? "bg-indigo-500/20" : "")
                    }
                    onClick={() => {
                      setSelectedLeadId(r.id);
                      setName(r.full_name || name);
                    }}
                  >
                    <div className="text-slate-100">{r.label || r.full_name}</div>
                    <div className="text-xs text-slate-200/60">{r.id}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={async () => {
              const fullName = String(name || "").trim();
              if (!fullName) return;

              // create lead if user didn't explicitly select one
              let leadId = selectedLeadId;
              if (!leadId) {
                // Create directly as a household lead so it is immediately recognized as Prospect 2+.
                if (householdId) {
                  const res = await api.post(`/households/${householdId}/leads`, { full_name: fullName });
                  leadId = res.data?.id;
                } else {
                  const res = await api.post("/leads", { full_name: fullName });
                  leadId = res.data?.id;
                }
              }

              if (householdId && leadId) {
                await api.put(`/leads/${leadId}`, { household_id: householdId });
              }

              onAdded?.({ lead_id: leadId, name: fullName });
              onOpenChange(false);
            }}
          >
            Add Prospect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
